/**
 * Consolidated trajectory chart for the patient detail view.
 *
 * Owns Chart.js setup (registration + plugins), the month-label
 * helpers, the planned-trajectory fallback for legacy goals, and the
 * render/hydrate lifecycle.
 *
 * - renderConsolidatedChart(goals) returns HTML that lays out one
 *   canvas per goal, grouped by category and honouring collapsed
 *   state.
 * - createTrajectoryCharts() walks the canvases in the DOM, destroys
 *   any prior Chart instances, and creates fresh ones with zoom/pan
 *   plus a shared singleton tooltip.
 *
 * Extracted from main.ts as part of OMON-031 / OMON-060 Phase 6a.
 */

import { type ChartConfiguration } from "chart.js";
// OMON-052: time-scale x-axis lets the trajectory chart show one point per
// observation at its real timestamp (rather than one bucket per calendar
// month). addMonths is needed for plan-window arithmetic.
import { addMonths } from "date-fns";

import { state } from "../state";
import type { GoalInfo, ActualObservation } from "../types";
import { escapeHtml } from "../utils/html";
// OMON-201 chart unification: Chart registration, colours, the singleton
// tooltip and the zoom config now live in chart-kit (shared with the
// monitored-event charts). Importing chart-kit guarantees registration.
import {
  Chart, CHART_COLORS, renderExternalTooltip, zoomConfig,
  clearSyncGroup, registerForSync, wireZoomControls, nowLinePlugin,
  setSyncKey, restoreRememberedView,
} from "./chart-kit";
// OMON-308: the x-axis window is now two ranges (what the chart opens on vs how
// far it may be panned/zoomed). The arithmetic is pure and lives apart so it is
// unit-testable without a DOM — same split as chart-sync / monitored-chart-data.
import { trajectoryBounds, todayCentredWindow, windowReachingData, latestActualMs } from "./trajectory-window";

export { TRAJECTORY_VIEW_MONTHS, trajectoryBounds, todayCentredWindow } from "./trajectory-window";
export type { TrajectoryWindow } from "./trajectory-window";

export function getPlannedTrajectory(goal: GoalInfo): number[] {
  if (goal._trajectory && goal._trajectory.length > 0) return goal._trajectory;
  const len = state.planMonths;
  const last = len - 1;
  const bl = goal._baselineMonths || 2;
  // OMON-275: typeof guards, not `||` — plan-side scores stay 1–5 today, but
  // `||` silently coerces a 0 and that idiom must not survive anywhere scores
  // flow now that recorded scores can legitimately be 0.
  const current = typeof goal.currentScore === "number" ? goal.currentScore : 1;
  const target = typeof goal.targetScore === "number" ? goal.targetScore : 3;
  const t = new Array(len).fill(0);
  t[0] = current;
  t[last] = target;
  for (let i = 1; i < bl && i < last; i++) t[i] = current;
  const rampStart = Math.max(0, bl - 1);
  const rampLen = last - rampStart;
  for (let i = rampStart + 1; i < last; i++) {
    const ratio = (i - rampStart) / rampLen;
    t[i] = Math.round(current + ratio * (target - current));
  }
  return t;
}

/** Get month labels starting from a given month offset within the current plan. */
export function getMonthLabelsFor(startOffset = 0, count?: number): string[] {
  const labels: string[] = [];
  const start = new Date(state.planStartDate);
  const n = count ?? state.planMonths;
  for (let i = 0; i < n; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + startOffset + i, 1);
    labels.push(d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" }));
  }
  return labels;
}

/** Build the HTML container for the trajectory chart. Chart.js canvases are created after DOM insertion. */
export function renderConsolidatedChart(goals: GoalInfo[]): string {
  // Group goals by category. OMON-028: within each category, goals are sorted
  // by the planner-controlled _order (falling back to title for legacy goals
  // that predate OMON-028). Category order comes from _goalCategory.order
  // via state.patientGoals already being sorted in loadPatientPlan.
  const grouped = new Map<string, GoalInfo[]>();
  for (const g of goals) {
    const cat = g.category || "Uncategorised";
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(g);
  }
  for (const catGoals of grouped.values()) {
    catGoals.sort((a, b) => {
      const oa = a._order ?? 999;
      const ob = b._order ?? 999;
      if (oa !== ob) return oa - ob;
      return (a.description || "").localeCompare(b.description || "");
    });
  }

  let html = '<div class="trajectory-charts">';

  for (const [cat, catGoals] of grouped) {
    const collapsed = state.chartCollapsedCategories.has(cat);
    const chevron = collapsed ? "▶" : "▼";
    const count = catGoals.length;

    html += `<div class="chart-category-header" data-chart-cat="${escapeHtml(cat)}">
      <span>${chevron}  ${escapeHtml(cat)}</span>
      <span class="chart-cat-count">${count}</span>
    </div>`;

    if (collapsed) continue;

    for (const g of catGoals) {
      const measureCode = g.measureCode || "";
      const smartGoal = g._smartGoal || "";
      // OMON-121: merged-row layout — the label area now carries the
      // metadata + tag bubbles that used to live in the separate "Goals"
      // section beneath the chart panel. Result: one self-contained row
      // per goal, no scrolling between chart and metadata to correlate.
      const metaParts: string[] = [];
      if (g.subcategory) metaParts.push(escapeHtml(g.subcategory));
      if (typeof g.targetScore === "number") metaParts.push(`Target: ${g.targetScore}/5`);
      if (typeof g.currentScore === "number") metaParts.push(`Assessment: ${g.currentScore}/5`);
      const metaLine = metaParts.join(" &middot; ");
      const achievementClass = achievementClassFor(g.achievementStatus);
      const lifecycleClass = g.lifecycleStatus === "active" ? "active" : "draft";
      html += `<div class="chart-goal-row">
        <div class="chart-goal-label">
          <div class="chart-goal-title">${escapeHtml(g.description)}</div>
          ${metaLine ? `<div class="chart-goal-meta" style="font-size:13px;color:#6b7280;margin-top:2px;">${metaLine}</div>` : ""}
          ${smartGoal ? `<div class="chart-goal-smart-goal">${escapeHtml(smartGoal)}</div>` : ""}
          <div class="chart-goal-chips" style="margin-top:6px;display:flex;gap:6px;flex-wrap:wrap;">
            <span class="achievement ${achievementClass}">${escapeHtml((g.achievementStatus || "").replace(/-/g, " "))}</span>
            <span class="badge badge-${lifecycleClass}">${escapeHtml(g.lifecycleStatus || "")}</span>
          </div>
        </div>
        <div class="chart-goal-canvas-wrap">
          <canvas data-goal-id="${g.docId}" data-measure-code="${escapeHtml(measureCode)}" height="80"></canvas>
        </div>
      </div>`;
    }
  }

  // OMON-121: zoom hint hoisted out of this function and rendered ONCE
  // above the tab strip in detail.ts. Removing it here avoids one hint
  // per chart category and keeps the merged-row list compact.
  html += '</div>';
  return html;
}

/**
 * OMON-121: shared achievement-status → CSS-class mapper, hoisted from
 * detail.ts so the merged trajectory row can render the same chip.
 */
function achievementClassFor(achievementStatus: string | undefined): string {
  return ({
    improving: "achievement-improving",
    "in-progress": "achievement-in-progress",
    "no-change": "achievement-no-change",
    achieved: "achievement-improving",
    "not-achieved": "achievement-worsening",
    worsening: "achievement-worsening",
  } as Record<string, string>)[achievementStatus || ""] || "achievement-in-progress";
}

/** Destroy all active Chart.js instances and create new ones from canvas elements. */
export function createTrajectoryCharts() {
  // Destroy previous instances
  for (const c of state.activeCharts) c.destroy();
  state.activeCharts = [];
  // The sync group holds references too — drop them with the instances, or a
  // rebuild would broadcast into destroyed charts.
  clearSyncGroup("trajectory");
  // OMON-371: a zoom is remembered per patient; a different patient forgets it.
  setSyncKey("trajectory", state.selectedPatient?.docId ?? "");
  // OMON-371: ONE window for every goal (they are synced): never open on an
  // empty month while this patient has scores.
  const latest = latestActualMs(state.patientActuals);

  document.querySelectorAll<HTMLCanvasElement>("[data-goal-id]").forEach((canvas) => {
    const goalId = canvas.getAttribute("data-goal-id")!;
    const measureCode = canvas.getAttribute("data-measure-code") || "";
    const goal = state.patientGoals.find((g) => g.docId === goalId);
    if (!goal) return;

    // OMON-308: the read view opens today-centred. "today" is opt-in per call
    // site — the plan builder deliberately keeps the pinned full-plan axis that
    // its editable month cells are measured against (plan-builder.ts,
    // layoutBuilderRow).
    const chart = buildTrajectoryChart(
      canvas,
      getPlannedTrajectory(goal),
      state.patientActuals[measureCode] || [],
      goal._baselineMonths || 2,
      "today",
      latest,
    );
    state.activeCharts.push(chart);
    const x = chart.options?.scales?.x as { min?: number; max?: number } | undefined;
    // Registered with the VIEW, not the bounds: this is the range "Reset view"
    // returns to, and the range the badge measures departures from.
    registerForSync("trajectory", chart, Number(x?.min ?? 0), Number(x?.max ?? 0));
  });

  wireZoomControls("trajectory", "trajectoryRange", "trajectoryResetZoom");
  // OMON-371: back from another tab → the zoom the reader left, not the default.
  // After wireZoomControls, so the badge and Reset button reflect it.
  restoreRememberedView("trajectory");
}

/**
 * OMON-187/188: build ONE trajectory Chart on a canvas from a planned series +
 * actuals. Extracted from createTrajectoryCharts so the plan-builder renders the
 * same chart per goal row (planned = the live editable cells; actuals =
 * state.patientActuals[goalName]; baselineMonths greys the assessment period).
 */
export function buildTrajectoryChart(
  canvas: HTMLCanvasElement,
  planned: number[],
  actuals: ActualObservation[],
  baselineMonths: number,
  view: "plan" | "today" = "plan",
  /** OMON-371: the patient's newest score across ALL goals (synced charts share one window). */
  patientLatestActualMs?: number,
): Chart {
  const planStart = new Date(state.planStartDate);
  const planEnd = addMonths(planStart, state.planMonths);

  // OMON-308: two ranges, not one. `bounds` is how far pan/zoom may reach;
  // `viewWindow` is what the chart opens on and what resetZoom() returns to.
  // view === "plan" reproduces the pre-OMON-308 behaviour exactly, which is
  // what the plan builder aligns its editable month cells against.
  const bounds = view === "today"
    ? trajectoryBounds(planStart, planEnd, actuals)
    : { min: planStart.getTime(), max: planEnd.getTime() };
  const viewWindow = view === "today"
    ? windowReachingData(todayCentredWindow(bounds), patientLatestActualMs)
    : bounds;

  {
    // Planned line: one point per month, plotted at the month-start date.
    // Indexes 0..planMonths-1 still align with segment.borderColor's
    // p1DataIndex check against baselineMonths.
    const plannedPoints = planned.map((y, i) => ({
      x: addMonths(planStart, i),
      y,
    }));

    // Actuals: one chart point per observation at its real timestamp.
    // Previous code bucketed by monthIndex into a sparse array, dropping all
    // but the last reading per month via dictionary collision. The new shape
    // preserves every observation, which is what zoom-to-day requires.
    const actualPoints = actuals.map((obs) => ({
      x: new Date(obs.date),
      y: obs.score,
    }));

    // Colour: blue when the observation has a note attached, green otherwise.
    // OMON-275: a score of 0 ("attempted to initiate — client unwilling/unable
    // to start") renders as a distinct red point regardless of note state —
    // it's an engagement event, not a performance level, and must not blend
    // into the trend visually.
    const pointBgColors = actuals.map((obs) =>
      obs.score === 0
        ? CHART_COLORS.attempted
        : obs.note ? CHART_COLORS.actualNote : CHART_COLORS.actual
    );
    // OMON-275: only widen the axis below 1 when a 0 actually exists — keeps
    // the 1–5 band uncompressed for the (vast) majority of goals without one.
    const hasZeroScore = actuals.some((obs) => obs.score === 0);

    const config = {
      type: "line" as const,
      data: {
        datasets: [
          {
            label: "Planned",
            data: plannedPoints,
            borderColor: CHART_COLORS.planned,
            borderWidth: 1.5,
            borderDash: [6, 4],
            pointRadius: 0,
            pointHoverRadius: 3,
            tension: 0,
            order: 2,
            segment: {
              // Grey out the dashed line during the assessment period (the
              // baseline-hold months, before the goal trajectory begins).
              borderColor: (ctx: { p1DataIndex: number }) =>
                ctx.p1DataIndex < baselineMonths
                  ? CHART_COLORS.plannedAssessment
                  : undefined,
            },
          },
          {
            label: "Actual",
            data: actualPoints,
            borderColor: CHART_COLORS.actual,
            borderWidth: 2.5,
            pointRadius: 4,
            pointHoverRadius: 7,
            pointBackgroundColor: pointBgColors,
            pointBorderColor: pointBgColors,
            pointBorderWidth: 1.5,
            tension: 0,
            spanGaps: true,
            order: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 300 },
        layout: { padding: { left: 4, right: 8, top: 4, bottom: 0 } },
        scales: {
          x: {
            type: "time" as const,
            time: {
              // OMON-052: don't pin to "month" — Chart.js auto-picks the unit
              // based on visible range. Zoomed out: month labels. Zoomed into
              // a week: day-of-month. Zoomed into a day: hour. minUnit gates
              // the floor at "hour" since observations don't have sub-hour
              // precision in our data model.
              minUnit: "hour" as const,
              displayFormats: { month: "MMM", week: "d MMM", day: "d MMM", hour: "HH:mm" },
              tooltipFormat: "d MMM yyyy, HH:mm",
            },
            min: viewWindow.min,
            max: viewWindow.max,
            grid: { display: false },
            ticks: { font: { size: 9 }, color: "#9ca3af", maxRotation: 0, autoSkip: true, autoSkipPadding: 12 },
          },
          y: {
            min: hasZeroScore ? -0.5 : 0.5,
            max: 5.5,
            ticks: {
              stepSize: 1,
              font: { size: 9 },
              color: "#d1d5db",
              callback: (val: string | number) =>
                (typeof val === "number" && val >= (hasZeroScore ? 0 : 1) && val <= 5) ? val.toString() : "",
            },
            grid: { color: CHART_COLORS.grid },
          },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            mode: "nearest" as const,
            intersect: false,
            // OMON-201: positioning + the singleton element are shared in
            // chart-kit; this closure only builds the dev-goal-specific rows.
            external: (context: unknown) =>
              renderExternalTooltip(context as Parameters<typeof renderExternalTooltip>[0], (tooltip) => {
                let html = "";
                if (tooltip.title?.length) {
                  html += `<div class="tt-title">${tooltip.title[0]}</div>`;
                }
                for (const item of tooltip.dataPoints || []) {
                  if (item.datasetIndex === 0) {
                    html += `<div class="tt-row"><span class="tt-label">Planned:</span> ${item.parsed.y}/5</div>`;
                  } else {
                    // OMON-052: dataIndex points directly into the actuals
                    // array (one chart point per observation).
                    const meta = actuals[item.dataIndex];
                    html += item.parsed.y === 0
                      ? `<div class="tt-row"><span class="tt-label">Score:</span> 0 — attempted, unable to start</div>`
                      : `<div class="tt-row"><span class="tt-label">Score:</span> ${item.parsed.y}/5</div>`;
                    if (meta?.performerName) {
                      html += `<div class="tt-row"><span class="tt-label">Carer:</span> ${meta.performerName}</div>`;
                    }
                    if (meta?.date) {
                      const d = new Date(meta.date);
                      html += `<div class="tt-row"><span class="tt-label">When:</span> ${d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</div>`;
                    }
                    if (meta?.note) {
                      html += `<div class="tt-note">${meta.note}</div>`;
                    }
                  }
                }
                return html;
              }),
          },
          zoom: zoomConfig(bounds.min, bounds.max, "trajectory"),
        },
        interaction: {
          mode: "nearest" as const,
          intersect: false,
          axis: "x" as const,
        },
      },
      // OMON-308: a "today" line, but only on the read view. The plan builder
      // draws its own (.builder-now-line), spanning the chart AND the editable
      // month cells beneath it — two markers would double up.
      plugins: view === "today" ? [nowLinePlugin()] : [],
    };

    // OMON-065: cast through `unknown` to satisfy Chart.js's strict generic
    // constraint. `config` uses `{x: Date, y: number}[]` because chartjs-
    // adapter-date-fns parses Date values, but Chart's signature only knows
    // about Point/BubbleDataPoint/number. Runtime is correct.
    return new Chart(canvas, config as unknown as ChartConfiguration);
  }
}
